
/**
 * Core navigation tracking functionality
 */
import { toast } from 'sonner';
import { navigationState, NAVIGATION_TIMEOUT } from './navigationState';
import { NavigationStats } from './types';

/**
 * Start tracking a navigation
 */
export const startNavigation = (path: string) => {
  // Don't start navigation if already navigating to the same path
  if (navigationState.inProgress && navigationState.currentPath === path) {
    console.log(`Already navigating to ${path}, ignoring duplicate request`);
    return;
  }
  
  // Clean up any existing timeout
  if (navigationState.timeoutId) {
    clearTimeout(navigationState.timeoutId);
  }
  
  // Set navigation state
  navigationState.inProgress = true;
  navigationState.startTime = Date.now();
  navigationState.currentPath = path;
  navigationState.navigationsCount++;
  
  console.log(`Navigation started: ${path}`);
  
  // Start loading indicator
  if (window._loadingIndicator) {
    window._loadingIndicator.startRequest();
  }
  
  // Set timeout to detect stuck navigations
  // Use 'as any' to handle the type mismatch between Timeout and number
  navigationState.timeoutId = setTimeout(() => {
    if (navigationState.inProgress) {
      const errorMessage = `Navigation timeout after ${NAVIGATION_TIMEOUT}ms: ${path}`;
      console.error(errorMessage);
      navigationState.errors.push(errorMessage);
      
      toast.error("Navigation is taking longer than expected. Please try again.");
      
      // Force end the navigation tracking
      endNavigation();
      
      // End any loading indicators
      if (window._loadingIndicator) {
        window._loadingIndicator.endAllRequests();
      }
    }
  }, NAVIGATION_TIMEOUT) as unknown as number;
};

/**
 * End tracking a navigation
 */
export const endNavigation = () => {
  const duration = navigationState.startTime 
    ? Date.now() - navigationState.startTime 
    : null;
  
  if (navigationState.currentPath) {
    console.log(`Navigation complete: ${navigationState.currentPath} (${duration}ms)`);
    navigationState.lastCompletedPath = navigationState.currentPath;
  }
  
  // Clear timeout
  if (navigationState.timeoutId) {
    clearTimeout(navigationState.timeoutId);
    navigationState.timeoutId = null;
  }
  
  // End loading indicator
  if (window._loadingIndicator) {
    window._loadingIndicator.endRequest();
  }
  
  // Reset state
  navigationState.inProgress = false;
  navigationState.startTime = null;
  navigationState.currentPath = null;
};

/**
 * Get navigation statistics
 */
export const getNavigationStats = (): NavigationStats => {
  return {
    navigationsCount: navigationState.navigationsCount,
    errors: [...navigationState.errors],
    currentlyNavigating: navigationState.inProgress,
    currentPath: navigationState.currentPath,
    lastCompletedPath: navigationState.lastCompletedPath
  };
};

// Expose a global function to end any navigation for emergency cleanup
window.endNavigation = endNavigation;
